import { useNavigate } from "@solidjs/router";
import { IoArrowForward, IoBusiness } from "solid-icons/io";
import { createMemo, createSignal, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/loading";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { RootLayout } from "@/domains/auth/routes/layout";
import { useCampaign, usePlans } from "@/domains/billing/api";
import type {
  BillingInterval,
  Plan,
  PlanSlug,
} from "@/domains/billing/api/types";
import { BillingIntervalToggle } from "@/domains/billing/components/BillingIntervalToggle";
import { CampaignBanner } from "@/domains/billing/components/CampaignBanner";
import { ContactSalesModal } from "@/domains/billing/components/ContactSalesModal";
import {
  PlanCard,
  type PlanCardCta,
} from "@/domains/billing/components/PlanCard";
import { PlanComparisonTable } from "@/domains/billing/components/PlanComparisonTable";
import { promoDiscountPercent } from "@/domains/billing/utils/campaign";
import {
  planIcon,
  usePlanSalesFeatures,
} from "@/domains/billing/utils/plan-presentation";
import { MetaTitleWithSeparator } from "@/domains/business/seo";

const GRID_SLUGS: PlanSlug[] = ["headhunter", "team"];

export default function PricingRoute() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [auth] = useAuthV2();
  const plans = usePlans();
  const campaignQuery = useCampaign();
  const [interval, setInterval] = createSignal<BillingInterval>("yearly");
  const [contactSalesOpen, setContactSalesOpen] = createSignal(false);

  const planFeatures = usePlanSalesFeatures();

  const isAuthenticated = () => !!auth.profile?.id;
  const campaign = () => campaignQuery.data?.campaign;

  // Only the two sellable paid plans go in the grid; Free is masked (D13).
  const gridPlans = createMemo<Plan[]>(() =>
    (plans.data?.plans ?? [])
      .filter((p) => GRID_SLUGS.includes(p.slug))
      .sort((a, b) => a.sort_order - b.sort_order),
  );

  // Comparison table keeps the paid + enterprise tiers, never Free.
  const comparisonPlans = createMemo<Plan[]>(() =>
    (plans.data?.plans ?? []).filter((p) => p.slug !== "free"),
  );

  const upgradeIntentUrl = (slug: PlanSlug) =>
    `/profile/company/upgrade?plan=${slug}&interval=${interval()}`;

  // CTA label uses the design's "Inizia gratis" copy; navigation carries the plan
  // intent (anonymous → signup?next=, authenticated → upgrade/checkout, D9/D21).
  const ctaFor = (plan: Plan): PlanCardCta => {
    const variant = plan.slug === "headhunter" ? "default" : "neutralOutline";
    const onClick = isAuthenticated()
      ? () => navigate(upgradeIntentUrl(plan.slug))
      : () =>
          navigate(
            `/auth/signup?next=${encodeURIComponent(upgradeIntentUrl(plan.slug))}`,
          );
    return { label: t("billing.sales.cta.start_free"), onClick, variant };
  };

  return (
    <RootLayout>
      <MetaTitleWithSeparator>{t("pricing.title")}</MetaTitleWithSeparator>

      <main class="min-h-screen bg-muted/30 pt-28 pb-16 lg:pt-32 lg:pb-20">
        <div class="mx-auto max-w-[1100px] px-4 sm:px-6">
          {/* header */}
          <div class="mb-[34px] text-center">
            <h1 class="whitespace-pre-line font-display text-5xl font-semibold leading-[0.96] tracking-[-0.03em] text-foreground sm:text-6xl">
              {t("billing.sales.pricing_hero_title")}
            </h1>
            <p class="mx-auto mt-4 max-w-[380px] text-base leading-relaxed text-muted-foreground">
              {t("billing.sales.pricing_hero_subtitle")}
            </p>
          </div>

          {/* billing interval toggle */}
          <div class="mb-[18px] flex justify-center">
            <BillingIntervalToggle value={interval()} onChange={setInterval} />
          </div>

          {/* campaign banner — only for an authenticated eligible early adopter */}
          <div class="mb-[18px] empty:mb-0">
            <CampaignBanner />
          </div>

          <Show when={plans.isLoading}>
            <Loading class="h-64" />
          </Show>

          <Show when={plans.isError}>
            <p class="mt-8 text-center text-muted-foreground">
              {t("pricing.error")}
            </p>
          </Show>

          <Show when={plans.data}>
            {/* two-plan grid */}
            <div class="mb-[18px] grid grid-cols-1 gap-[18px] md:grid-cols-2">
              <For each={gridPlans()}>
                {(plan) => (
                  <PlanCard
                    plan={plan}
                    interval={interval()}
                    discountPercent={promoDiscountPercent(
                      campaign(),
                      plan.slug,
                    )}
                    lockedForLife={campaign()?.price_locked_for_life}
                    recommended={plan.slug === "headhunter"}
                    features={planFeatures(plan.slug)}
                    icon={planIcon(plan.slug)}
                    priceSubtext={
                      plan.slug === "team"
                        ? t("billing.sales.per_team")
                        : undefined
                    }
                    tag={
                      plan.slug === "team"
                        ? t("billing.sales.features.team_pricing")
                        : undefined
                    }
                    showTrialNote
                    cta={ctaFor(plan)}
                  />
                )}
              </For>
            </div>

            {/* enterprise contact card */}
            <div class="mb-6 flex flex-wrap items-center justify-between gap-6 rounded-[18px] border border-border bg-white px-7 py-6 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
              <div class="flex min-w-0 items-center gap-[18px]">
                <div class="inline-flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-[14px] bg-primary/[0.08]">
                  <IoBusiness class="h-7 w-7 text-primary" />
                </div>
                <div>
                  <h3 class="font-display text-2xl font-semibold tracking-[-0.01em] text-foreground">
                    {t("billing.sales.enterprise_card.heading")}
                  </h3>
                  <p class="mt-1 max-w-[540px] text-[14.5px] text-muted-foreground">
                    {t("billing.sales.enterprise_card.description")}
                  </p>
                </div>
              </div>
              <Button
                class="h-[46px] shrink-0 gap-2 rounded-[11px] px-[22px] text-[15px] font-semibold"
                onClick={() => setContactSalesOpen(true)}
              >
                {t("billing.sales.enterprise_card.cta")}
                <IoArrowForward class="h-4 w-4" />
              </Button>
            </div>

            {/* footer trust line */}
            <p class="text-center text-[13.5px] text-muted-foreground">
              {t("billing.sales.footer_trust")}
            </p>

            {/* comparison table (kept below the design surface) */}
            <div class="mt-10 hidden justify-center lg:flex">
              <a
                href="#comparison-table"
                class="text-sm text-primary hover:underline"
              >
                {t("pricing.comparison.anchor")}
              </a>
            </div>
            <section id="comparison-table" class="mt-12">
              <h2 class="mb-6 text-center text-2xl font-semibold text-foreground">
                {t("pricing.comparison.title")}
              </h2>
              <PlanComparisonTable plans={comparisonPlans()} />
            </section>
          </Show>
        </div>
      </main>
      <ContactSalesModal
        isOpen={contactSalesOpen()}
        onClose={() => setContactSalesOpen(false)}
      />
    </RootLayout>
  );
}
